import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { Box, Stack, Dialog, IconButton } from "@mui/material";
import Phase from "./Phase";
import AnimationTimeline from "./AnimationTimelineV2";
import fscreen from "fscreen";
import { useWindowSize } from "usehooks-ts";
import Helper from "../functions/Helper.js";
import { useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faTimes } from "@fortawesome/pro-regular-svg-icons";
import { faChevronRight } from "@fortawesome/pro-regular-svg-icons";
import useCurrentValue from "../../hooks/useCurrentValue";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import * as rdd from "react-device-detect";
import FullPlayAnimator from "./FullPlayAnimator.js";
import FullPlayAnimationTimeline from "./FullPlayAnimationTimelineV2.js";

const darkTheme = createTheme({
  palette: {
    mode: "dark",
  },
});
const FullScreenMode = ({
  mode,
  fullScreenRef,
  setIsFullscreen,
  isFullscreen,
  children,
  prefix,
}) => {
  if (mode === "browser") {
    return (
      <Box
        id={prefix}
        sx={{
          position: "absolute",
          top: 0,
          zIndex: 10000000000,
          display: "flex",
        }}
        ref={fullScreenRef}
      >
        {isFullscreen && children}
      </Box>
    );
  } else {
    return (
      <Dialog
        PaperProps={{
          style: { backgroundColor: "black" },
        }}
        fullScreen
        onClose={() => setIsFullscreen(false)}
        open={isFullscreen}
      >
        {isFullscreen && children}
      </Dialog>
    );
  }
};

const headerHeight = 60;
const timelineHeight = 90;

const FullScreenPhase = forwardRef(
  (
    { mirror, setMirror, phases, settings, prefix = "", showFullPlay = false },
    ref
  ) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [currentPhaseIndex, setCurrentPhaseIndex] = useState(0);

    const size = useWindowSize();
    const fullScreenRef = useRef();

    const currentIsFullscreen = useCurrentValue(isFullscreen);

    let { viewBoxWidth, viewBoxHeight } = Helper.getMaxCoordinates(
      settings.editor.court
    );

    const calculatedHeight = Math.min(
      size.height,
      size.width * (viewBoxHeight / viewBoxWidth)
    );

    const finalHeight = Math.min(
      size.height - (20 + timelineHeight + (showFullPlay ? 0 : headerHeight)),
      calculatedHeight
    );

    useEffect(() => {
      function onFullscreenChange(event) {
        if (!Boolean(fscreen.fullscreenElement)) {
          setIsFullscreen(false);
        }
      }
      fscreen.addEventListener("fullscreenchange", onFullscreenChange);
      return () =>
        fscreen.removeEventListener("fullscreenchange", onFullscreenChange);
    }, []);

    useImperativeHandle(ref, () => ({
      requestFullscreen() {
        setIsFullscreen(true);
        if (mode === "browser") {
          fscreen.requestFullscreen(fullScreenRef.current);
        }
      },
      setCurrentPhaseIndex(x) {
        setCurrentPhaseIndex(x);
      },
    }));

    const phase = phases[currentPhaseIndex];

    //add event listeners so that arrow up and down can be used to change phases
    useEffect(() => {
      function handleKeyDown(e) {
        if (currentIsFullscreen.current) {
          if (e.key === "ArrowLeft") {
            setCurrentPhaseIndex((current) => Math.max(0, current - 1));
          }
          if (e.key === "ArrowRight") {
            setCurrentPhaseIndex((current) =>
              Math.min(phases.length - 1, current + 1)
            );
          }
          if (e.key === "Escape") {
            setIsFullscreen(false);
          }
        }
      }
      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    const mode = fscreen.fullscreenEnabled && !rdd.isIOS ? "browser" : "dialog";
    //const mode = "dialog";

    return (
      <FullScreenMode
        prefix={prefix}
        setIsFullscreen={setIsFullscreen}
        isFullscreen={isFullscreen}
        mode={mode}
        //mode={rdd.isDesktop ? "desktop" : "mobile"}
        fullScreenRef={fullScreenRef}
      >
        <Box
          sx={{
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            flex: 1,
          }}
        >
          <Box sx={{ position: "relative" }}>
            {mode === "dialog" && (
              <IconButton
                size="large"
                onClick={() => {
                  setIsFullscreen(false);
                  if (fscreen.fullscreenElement) {
                    fscreen.exitFullscreen();
                  }
                }}
                sx={(theme) => ({
                  color: theme.palette.grey[300],
                  position: "absolute",
                  top: showFullPlay ? 10 : 0,
                  bottom: 0,
                  right: 5,
                })}
              >
                <FontAwesomeIcon fontSize={30} icon={faTimes} />
              </IconButton>
            )}

            {!showFullPlay && (
              <Stack
                sx={(theme) => ({
                  fontWeight: 600,
                  background: "black",
                  color: theme.palette.grey[300],
                  height: headerHeight,
                })}
                direction="row"
                justifyContent="center"
                alignItems="center"
              >
                <Box sx={{ textAlign: "center", width: 50 }}>
                  <IconButton
                    disabled={currentPhaseIndex === 0}
                    sx={(theme) => ({ color: theme.palette.grey[300] })}
                    onClick={() =>
                      setCurrentPhaseIndex((current) => current - 1)
                    }
                    size="small"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </IconButton>
                </Box>
                <Box sx={{ fontSize: 22 }}>
                  {Helper.getPhaseTitle(phase, phases, "output")}
                </Box>
                <Box sx={{ width: 50 }}>
                  <IconButton
                    disabled={currentPhaseIndex === phases.length - 1}
                    sx={(theme) => ({ color: theme.palette.grey[200] })}
                    onClick={() =>
                      setCurrentPhaseIndex((current) => current + 1)
                    }
                    size="small"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </IconButton>
                </Box>
              </Stack>
            )}
          </Box>
          <Box
            sx={{
              margin: "auto",
              width: finalHeight * (viewBoxWidth / viewBoxHeight),
            }}
          >
            {showFullPlay ? (
              <FullPlayAnimator
                mirror={mirror}
                prefix={prefix + "_fullscreen_full_play_"}
                showTitle={false}
                title={""}
                data={{
                  phases: {
                    phases: phases,
                  },
                  settings: settings,
                }}
              />
            ) : (
              <Phase
                mirror={mirror}
                id={phase.id}
                uId={prefix + "_full_screen"}
                editable={false}
                animatable={true}
                objectSizeFactor={settings.objectSizeFactor}
                courtColor={settings.courtColor}
                court={settings.editor.court}
                dispatch={() => {}}
                phase={phase}
              />
            )}
          </Box>

          <Box
            sx={{
              height: timelineHeight,
            }}
          >
            <ThemeProvider theme={darkTheme}>
              {showFullPlay ? (
                <FullPlayAnimationTimeline
                  mode="fullscreen"
                  requestFullscreen={() => {
                    setIsFullscreen(false);
                    if (fscreen.fullscreenElement) {
                      fscreen.exitFullscreen();
                    }
                  }}
                  mirror={mirror}
                  setMirror={setMirror}
                  showOverlay={settings?.showOverlay ?? true}
                  prefix={prefix + "_fullscreen_full_play_"}
                  phases={phases}
                  editor={settings.editor}
                />
              ) : (
                <AnimationTimeline
                  mode="fullscreen"
                  requestFullscreen={() => {
                    setIsFullscreen(false);

                    if (fscreen.fullscreenElement) {
                      fscreen.exitFullscreen();
                    }
                  }}
                  containerRef={fullScreenRef}
                  mirror={mirror}
                  setMirror={setMirror}
                  updateSteps={() => {}}
                  prefix={prefix + "_full_screen"}
                  phase={phase}
                  editor={settings.editor}
                />
              )}
            </ThemeProvider>
          </Box>
        </Box>
      </FullScreenMode>
    );
  }
);

export default FullScreenPhase;
